/**
 * Transformation utilities for converting backend LLMHistory to frontend FlatContent.
 * Part of Phase 3.5: Backend Integration for dexie-backed chat component.
 */

import type { LLMHistory, LLMHistoryToolUseContent, LLMHistoryFileContent } from "@bloomstack/apis/llmTypes";
import type { FlatContent, MessageHeaderData, ToolExecutionData, FileGroupData } from "@/types/streamTypes";

// ============================================================================
// CONTENT ID GENERATION
// ============================================================================

/**
 * Discriminated union for content ID generation parameters.
 * Each type requires different fields to generate a unique ID.
 */
export type ContentIdParams =
  | { type: 'header'; entryId: string }
  | { type: 'regular'; entryId: string; contentIndex: number }
  | { type: 'tool_progress'; requestId: string }
  | { type: 'file_group'; entryId: string; firstContentIndex: number };

/**
 * Hub function for generating content IDs.
 * Routes to the correct ID pattern based on the params type.
 * 
 * @param params - Discriminated union with type-specific fields
 * @returns Generated content ID string for Dexie primary key
 */
export function generateContentId(params: ContentIdParams): string {
  switch (params.type) {
    case 'header':
      return `${params.entryId}-header`;
    case 'regular':
      return `${params.entryId}-${params.contentIndex}`;
    case 'tool_progress':
      return `tool-progress-${params.requestId}`;
    case 'file_group':
      return `${params.entryId}-filegroup-${params.firstContentIndex}`;
  }
}

// ============================================================================
// ROOT ENTRY RESOLUTION
// ============================================================================

/**
 * Find the root entry ID by walking up the parent chain.
 * Used for grouping all descendants under one message header for edit operations.
 *
 * @param entryId - The entry to find the root for
 * @param entries - All LLMHistory entries (needed to walk the parent chain)
 * @param visited - Set of visited entry IDs to prevent infinite loops
 * @returns The root entry ID (entry with no parent)
 */
function findRootEntryId(
  entryId: string,
  entryMap: Map<string, LLMHistory>,
  rootCache: Map<string, string>,
  visited = new Set<string>()
): string {
  // Already resolved
  if (rootCache.has(entryId)) return rootCache.get(entryId)!;

  // Prevent infinite loops in case of circular references
  if (visited.has(entryId)) return entryId;
  visited.add(entryId);

  const entry = entryMap.get(entryId); // O(1) lookup

  // If entry not found or has no parent, this is the root
  if (!entry || !entry.parentId) {
    rootCache.set(entryId, entryId);
    return entryId;
  }

  // Walk up the parent chain, cache the result
  const root = findRootEntryId(entry.parentId, entryMap, rootCache, visited);
  rootCache.set(entryId, root);
  return root;
}

// ============================================================================
// TRANSFORM OPTIONS
// ============================================================================

/**
 * Options for llmHistoryToFlatContent transformation
 */
export interface TransformOptions {
  /**
   * Browser arrival timestamp to set on all created FlatContent objects.
   * 
   * - Set to Date.now() for real-time RPC events
   * - Omit for historical batch loads (will be undefined in output)
   * 
   * Used by resolvers to determine which content version is newer.
   * undefined = historical data (treated as -Infinity for comparisons)
   */
  arrivedAt?: number;
}

/**
 * Converts backend LLMHistory array to frontend FlatContent array for IndexedDB storage.
 * 
 * Handles:
 * - Synthetic message_header rows (one per LLMHistory entry)
 * - Flattening content arrays into individual rows
 * - Pairing tool_use/tool_result into tool_execution synthetic rows across entries
 * - Streaming cases: emits partial tool_execution that bulkUpsert will merge
 * 
 * @param history - Array of LLMHistory entries from backend
 * @param options - Transform options
 * @returns Array of FlatContent rows ready for Dexie insertion
 */
export function llmHistoryToFlatContent(
  history: LLMHistory[],
  options?: TransformOptions
): FlatContent[] {
  const { arrivedAt } = options ?? {};
  const result: FlatContent[] = [];
  
  // Map requestId → index in result array (for pairing tool_use/tool_result across entries)
  const toolExecutionIndex = new Map<string, number>();

  // entryId → LLMHistory for O(1) lookups during root resolution
  const entryMap = new Map<string, LLMHistory>(history.map((e) => [e.id!, e]));
  // Cache resolved roots so sibling/child entries don't re-walk the same chain
  const rootCache = new Map<string, string>();

  for (const entry of history) {
    const {
      id: entryId,
      sequenceNum,
      role,
      userProfileId: userId,
      timestamp,
      internal,
      streamId,
      userProfile,
      content,
    } = entry;

    // Skip entries without required fields
    // Note: userId can be empty string for system entries — only reject undefined/null
    if (!entryId || userId == null || !timestamp || !streamId) {
      if (process.env.NODE_ENV === "development") {
        console.warn(`Skipping entry with missing required fields:`, {
          entry,
          missingFields: {
            id: !entryId,
            user: !userId,
            timestamp: !timestamp,
            streamId: !streamId,
          },
        });
      }
      continue;
    }

    // Calculate root entry ID once for this entry (walks up parent chain)
    const rootEntryId = findRootEntryId(entryId, entryMap, rootCache);

    // Generate synthetic message_header row for all entries.
    // Child entry headers are filtered out in useDataStream but exist for metadata lookup.
    const headerData: MessageHeaderData = {
      role,
      userName: userProfile?.name ?? "Unknown",
      avatarUrl: userProfile?.avatar,
      userProfile,  // Store original for sync metadata extraction
    };

    const headerRow = {
      contentId: generateContentId({ type: 'header', entryId }),
      streamId,
      entryId,
      parentEntryId: entry.parentId ?? null,
      rootEntryId,
      userProfileId: userId,
      role,
      timestamp,
      internal: internal ?? false,
      sequenceNum: sequenceNum ?? 0,
      contentIndex: -1, // Headers use -1 for ordering before content
      type: "message_header" as const,
      data: headerData,
      ...(arrivedAt !== undefined && { arrivedAt }),
    };

    result.push(headerRow);

    // Track pending file group for grouping consecutive file items
    let pendingFileGroup: LLMHistoryFileContent[] = [];
    let firstFileContentIndex: number | null = null;

    /**
     * Flush pending file group to result array.
     * Creates a single file_group row from accumulated file items.
     */
    const flushFileGroup = () => {
      if (pendingFileGroup.length === 0) return;

      const fileGroupData: FileGroupData = {
        files: [...pendingFileGroup],
      };

      result.push({
        contentId: generateContentId({ type: 'file_group', entryId, firstContentIndex: firstFileContentIndex! }),
        streamId,
        entryId,
        parentEntryId: entry.parentId ?? null,
        rootEntryId,
        userProfileId: userId,
        role,
        timestamp,
        internal: internal ?? false,
        sequenceNum: sequenceNum ?? 0,
        contentIndex: firstFileContentIndex!,
        type: "file_group",
        data: fileGroupData,
        ...(arrivedAt !== undefined && { arrivedAt }),
      });

      // Reset pending state
      pendingFileGroup = [];
      firstFileContentIndex = null;
    };

    // Flatten content array
    for (let i = 0; i < content.length; i++) {
      const contentItem = content[i];
      // Use array position (i) as contentIndex — this is the stable position within the entry.
      // orderIndex is a backend ordering hint but FlatContent.contentIndex is position-based.
      const contentIndex = i;

      if (contentItem.type === "tool_use") {
        // Flush any pending file group before tool_use
        flushFileGroup();

        // Create tool_execution immediately with toolUse data
        const toolExecutionData: ToolExecutionData = {
          toolUse: contentItem,
          toolResult: undefined,
          status: "streaming",
          startTime: Date.parse(timestamp),
          toolUseEntryId: entryId,
        };

        result.push({
          contentId: generateContentId({ type: 'tool_progress', requestId: contentItem.requestId }),
          streamId,
          entryId,
          parentEntryId: entry.parentId ?? null,
          rootEntryId,
          userProfileId: userId,
          role,
          timestamp,
          internal: internal ?? false,
          sequenceNum: sequenceNum ?? 0,
          contentIndex,
          type: "tool_execution",
          data: toolExecutionData,
          ...(arrivedAt !== undefined && { arrivedAt }),
        });

        // Store index so tool_result can update this entry
        toolExecutionIndex.set(contentItem.requestId, result.length - 1);

      } else if ((contentItem as any).type === "tool_result") {
        // Flush any pending file group before tool_result
        flushFileGroup();
        const toolResultItem = contentItem as any;

        const existingIndex = toolExecutionIndex.get(toolResultItem.requestId);

        if (existingIndex !== undefined) {
          // Found matching tool_use - update the existing tool_execution in place
          const existing = result[existingIndex];
          const existingData = existing.data as ToolExecutionData;
          
          existing.data = {
            ...existingData,
            toolResult: toolResultItem,
            status: toolResultItem.failed ? "error" : "complete",
            endTime: Date.parse(timestamp),
            toolResultEntryId: entryId,
          };
        } else if (arrivedAt !== undefined) {
          // No matching tool_use — real-time mode only.
          // Create partial tool_execution so bulkUpsert can merge with the existing row.
          // In batch/historical mode (arrivedAt === undefined) we skip this: the full
          // history load should always have the paired tool_use in the same batch.
          const toolExecutionData: ToolExecutionData = {
            toolUse: undefined,
            toolResult: toolResultItem,
            status: toolResultItem.failed ? "error" : "complete",
            startTime: Date.parse(timestamp),
            endTime: Date.parse(timestamp),
            toolResultEntryId: entryId,
          };

          result.push({
            contentId: generateContentId({ type: 'tool_progress', requestId: toolResultItem.requestId }),
            streamId,
            entryId,
            parentEntryId: entry.parentId ?? null,
            rootEntryId,
            userProfileId: userId,
            role,
            timestamp,
            internal: internal ?? false,
            sequenceNum: sequenceNum ?? 0,
            contentIndex,
            type: "tool_execution",
            data: toolExecutionData,
            ...(arrivedAt !== undefined && { arrivedAt }),
          });
        }
      } else if (contentItem.type === "file") {
        // Accumulate file items into pending group.
        // Use orderIndex (not array position) for firstFileContentIndex so that the
        // file_group is ordered correctly relative to other content items with their
        // own orderIndex values (e.g. text at orderIndex 0, files at orderIndex 5+).
        if (firstFileContentIndex === null) {
          firstFileContentIndex = (contentItem as any).orderIndex ?? i;
        }
        pendingFileGroup.push(contentItem as LLMHistoryFileContent);
      } else {
        // All other content types (text, thinking, etc.)
        // Flush any pending file group first
        flushFileGroup();

        // Store directly
        const rowId = generateContentId({ type: 'regular', entryId, contentIndex });
        result.push({
          contentId: rowId,
          streamId,
          entryId,
          parentEntryId: entry.parentId ?? null,
          rootEntryId,
          userProfileId: userId,
          role,
          timestamp,
          internal: internal ?? false,
          sequenceNum: sequenceNum ?? 0,
          contentIndex,
          type: contentItem.type,
          data: contentItem,
          ...(arrivedAt !== undefined && { arrivedAt }),
        });
      }
    }

    // Flush any remaining file group at end of content array
    flushFileGroup();
  }

  return result;
}

/**
 * Helper to validate that all tool_execution blocks are properly formed.
 * Useful for debugging streaming scenarios.
 */
export function hasIncompleteToolExecutions(flatContent: FlatContent[]): boolean {
  const toolExecutions = flatContent.filter(
    (row) => row.type === "tool_execution"
  );

  return toolExecutions.some((row) => {
    const data = row.data as ToolExecutionData;
    return data.status === "streaming" || data.status === "pending";
  });
}
